import React, { useState } from 'react';
import axios from 'axios';
import { useAccount, useSignTypedData } from 'wagmi';
import { Ban, Loader2, Wallet } from 'lucide-react';
import { useWallet } from '@/context/WalletContext';
import { fetchDemoConfig } from '@/lib/bindingConsent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/binding`;

export function RevokeButton({ receipt, onRevoked }) {
  const { address, isConnected } = useAccount();
  const { connectWallet } = useWallet();
  const { signTypedDataAsync } = useSignTypedData();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (receipt.status !== 'unused') return null;
  const isSigner = isConnected && address?.toLowerCase() === receipt.principal.toLowerCase();

  const revoke = async () => {
    setBusy(true); setError(null);
    try {
      const cfg = await fetchDemoConfig();
      const revokedAt = Math.floor(Date.now() / 1000);
      const signature = await signTypedDataAsync({
        domain: cfg.domain, types: cfg.revoke_types, primaryType: 'RevokeConsent',
        message: { receiptId: receipt.receipt_id, principal: receipt.principal, revokedAt: BigInt(revokedAt) },
      });
      await axios.post(`${API}/receipts/${receipt.receipt_id}/revoke`, { revokedAt, signature });
      onRevoked();
    } catch (e) {
      setError(e?.shortMessage || e?.response?.data?.detail || e?.message || 'Revocation failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="receipt-revoke-card">
      <div className="text-sm font-semibold text-white">Changed your mind?</div>
      <p className="text-xs text-gray-400 mt-1 mb-3">The signer can cancel this receipt before it is used. It is free, moves no money, and the gate refuses the receipt from then on. A revocation cannot be undone.</p>
      {isSigner ? (
        <button type="button" onClick={revoke} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-red-500/50 text-red-300 hover:bg-red-500/10 disabled:opacity-40 px-4 py-2 text-xs font-semibold" data-testid="receipt-revoke-button">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />} Revoke this receipt
        </button>
      ) : (
        <button type="button" onClick={connectWallet} className="inline-flex items-center gap-2 text-xs font-semibold text-gray-300 border border-white/15 rounded-full px-3 py-1.5" data-testid="receipt-revoke-connect">
          <Wallet className="w-3.5 h-3.5" /> {isConnected ? 'Connected wallet is not the signer' : 'Signer: connect wallet to revoke'}
        </button>
      )}
      {error && <p className="mt-2 text-xs text-red-400" data-testid="receipt-revoke-error">{error}</p>}
    </div>
  );
}
